// src/settings/RegionManagement.js
import React, { useState, useEffect } from 'react';
import ConfirmationDialog from './components/ConfirmationDialog';
import { useNotification } from '../context/NotificationContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  useTheme, // Import useTheme for breakpoints
  useMediaQuery, // Import useMediaQuery for breakpoints
  Card, // Import Card for mobile view
  CardContent, // Import CardContent for mobile view
  CardActions, // Import CardActions for mobile view
  Tooltip // Import Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const RegionManagement = () => {
  const { showNotification } = useNotification();
  
  // State for regions
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState(null);
  
  // State for dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedRegion, setSelectedRegion] = useState(null);
  
  // State for form
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    countries: [],
    states: []
  });
  
  // State for reference data
  const [referenceData, setReferenceData] = useState({
    countries: [],
    states: {}
  });
  
  // State for selected countries and states
  const [selectedCountries, setSelectedCountries] = useState([]);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen is extra-small or smaller
  const [selectedStates, setSelectedStates] = useState({});
  
  // Fetch regions on component mount
  useEffect(() => {
    fetchRegions();
    fetchReferenceData();
  }, []);
  
  // Fetch regions from API
  const fetchRegions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/regions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setRegions(response.data);
    } catch (err) {
      console.error('Error fetching regions:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to fetch regions. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch reference data (countries and states)
  const fetchReferenceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/regions/reference-data`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setReferenceData(response.data);
    } catch (err) {
      console.error('Error fetching reference data:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to fetch reference data. Please try again.';
      showNotification(errorMessage, 'error');
    }
  };
  
  // Open dialog for creating a new region
  const handleCreateRegion = () => {
    setDialogMode('create');
    setSelectedRegion(null);
    setFormData({
      name: '',
      description: '',
      countries: [],
      states: []
    });
    setSelectedCountries([]);
    setSelectedStates({});
    setDialogOpen(true);
  };
  
  // Open dialog for editing an existing region
  const handleEditRegion = (region) => {
    setDialogMode('edit');
    setSelectedRegion(region);
    
    // Extract countries and states from region
    const countries = region.countries || [];
    const states = region.states || [];
    
    // Create selectedStates object
    const statesByCountry = {};
    states.forEach(state => {
      if (!statesByCountry[state.country]) {
        statesByCountry[state.country] = [];
      }
      statesByCountry[state.country].push(state.state);
    });
    
    setFormData({
      name: region.name || '',
      description: region.description || '',
      countries,
      states
    });
    
    setSelectedCountries(countries);
    setSelectedStates(statesByCountry);
    
    setDialogOpen(true);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle country selection change
  const handleCountryChange = (event) => {
    const { value } = event.target;
    setSelectedCountries(value);
    
    // Remove states for countries that are no longer selected
    const newSelectedStates = { ...selectedStates };
    Object.keys(newSelectedStates).forEach(country => {
      if (!value.includes(country)) {
        delete newSelectedStates[country];
      }
    });
    
    setSelectedStates(newSelectedStates);
  };
  
  // Handle state selection change for a specific country
  const handleStateChange = (country, event) => {
    const { value } = event.target;
    setSelectedStates(prev => ({
      ...prev,
      [country]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data for API
      const apiData = {
        name: formData.name,
        description: formData.description,
        countries: selectedCountries,
        states: []
      };
      
      // Add states
      Object.entries(selectedStates).forEach(([country, states]) => {
        states.forEach(state => {
          apiData.states.push({
            country,
            state
          });
        });
      });
      
      if (dialogMode === 'create') {
        // Create new region
        await axios.post(`${process.env.REACT_APP_API_URL}/api/regions`, apiData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        // Update existing region
        await axios.put(`${process.env.REACT_APP_API_URL}/api/regions/${selectedRegion._id}`, apiData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // Refresh regions
      fetchRegions();
      
      // Close dialog
      handleDialogClose();
      
      // Show success notification
      showNotification(
        dialogMode === 'create' ? 'Region created successfully!' : 'Region updated successfully!',
        'success'
      );
    } catch (err) {
      console.error('Error saving region:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save region. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening delete confirmation dialog
  const handleDeleteClick = (region) => {
    setRegionToDelete(region);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!regionToDelete) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/regions/${regionToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh regions
      fetchRegions();
      
      // Show success notification
      showNotification('Region deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting region:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to delete region. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setRegionToDelete(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRegionToDelete(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Region Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateRegion}
          data-tour="regions-add"
        >
          Create Region
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}
      
      {!loading && regions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">No regions found. Create your first region!</Typography>
        </Paper>
      ) : (
        <> {/* Use Fragment to return one element */}
          {!isXs && (
            // --- Table View ---
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1, px: 2 }}>Name</TableCell>
                    <TableCell sx={{ py: 1, px: 2 }}>Description</TableCell>
                    <TableCell sx={{ py: 1, px: 2 }}>Countries</TableCell>
                    <TableCell sx={{ py: 1, px: 2 }}>States/Provinces</TableCell>
                    <TableCell sx={{ py: 1, px: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regions.map((region) => (
                    <TableRow key={region._id}>
                      <TableCell sx={{ py: 1.5, px: 2 }}>{region.name}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>{region.description}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {region.countries?.map((country) => (
                          <Chip
                            key={country}
                            label={referenceData.countries.find(c => c.code === country)?.name || country}
                            size="small"
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {region.states?.length > 0 ? (
                          `${region.states.length} states/provinces`
                        ) : (
                          'None'
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Tooltip title="Edit Region">
                          <IconButton aria-label={`Edit region ${region.name}`} onClick={() => handleEditRegion(region)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Region">
                          <IconButton aria-label={`Delete region ${region.name}`} onClick={() => handleDeleteClick(region)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {isXs && (
             // --- Card View ---
            <Box>
              {regions.map((region) => (
                <Card key={region._id} sx={{ mb: 2 }} elevation={2}>
                  <CardContent sx={{ pb: 1 }}>
                     <Typography variant="h6" component="div" gutterBottom>
                       {region.name}
                     </Typography>
                     {region.description && (
                       <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                         {region.description}
                       </Typography>
                     )}
                     <Box sx={{ mb: 1 }}>
                       <Typography variant="caption" display="block">Countries:</Typography>
                       {region.countries?.length > 0 ? region.countries.map((country) => (
                         <Chip
                           key={country}
                           label={referenceData.countries.find(c => c.code === country)?.name || country}
                           size="small"
                           sx={{ m: 0.5 }}
                         />
                       )) : <Typography variant="caption" sx={{ fontStyle: 'italic' }}>None</Typography>}
                     </Box>
                     <Box>
                       <Typography variant="caption" display="block">States/Provinces:</Typography>
                       {region.states?.length > 0 ? (
                         <Typography variant="caption">
                           {region.states.length} selected
                         </Typography>
                       ) : (
                         <Typography variant="caption" sx={{ fontStyle: 'italic' }}>None</Typography>
                       )}
                     </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                     <Tooltip title="Edit Region">
                       <IconButton aria-label={`Edit region ${region.name}`} size="small" onClick={() => handleEditRegion(region)}>
                         <EditIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                     <Tooltip title="Delete Region">
                       <IconButton aria-label={`Delete region ${region.name}`} size="small" onClick={() => handleDeleteClick(region)}>
                         <DeleteIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}
      
      {/* Region Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Region' : 'Edit Region'}
        </DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Region Name"
                  name="name"
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Countries</InputLabel>
                  <Select
                    multiple
                    value={selectedCountries}
                    onChange={handleCountryChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={referenceData.countries.find(c => c.code === value)?.name || value} 
                            size="small" 
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {referenceData.countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* States/Provinces Selection */}
              {selectedCountries.map((country) => (
                <Grid item xs={12} key={country}>
                  <Typography variant="subtitle1" gutterBottom>
                    {referenceData.countries.find(c => c.code === country)?.name || country} States/Provinces
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>{`Select States/Provinces`}</InputLabel>
                    <Select
                      multiple
                      value={selectedStates[country] || []}
                      onChange={(e) => handleStateChange(country, e)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={referenceData.states[country]?.find(s => s.code === value)?.name || value} 
                              size="small" 
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {referenceData.states[country]?.map((state) => (
                        <MenuItem key={state.code} value={state.code}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            disabled={loading || !formData.name || selectedCountries.length === 0}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Region"
        content={`Are you sure you want to delete the region "${regionToDelete?.name}"? This action cannot be undone.`}
        confirmColor="error"
      />
    </Box>
  );
};

export default RegionManagement;
